/**
 * Coherence rule: a training contract (apprendistato / tirocinio-stage) exists to
 * FORM someone in the role, so requiring real prior experience in that same role
 * is a direct contradiction.
 *
 * This mirrors the backend deterministic gate in
 * `AdValidator.check_training_contract_experience/1` (core-services, PR #280),
 * whose allowlist is the source of truth. Keep the two in sync: the experience
 * threshold below is the single knob for what counts as "requires prior
 * experience". The backend keeps its own CI test asserting every non-zero
 * experience label blocks; if you add an experience key here or there, update
 * both. Experience keys are defined canonically in the backend
 * `RestWorld.ReleaseTasks.Variables` variable_name "experience" list.
 */

// Contract types considered "training" (Variable.key values from the backend).
export const TRAINING_CONTRACT_KEYS = ['internship', 'apprenticeship'];

// Experience Variable.key values that represent ANY prior-experience-in-role
// requirement, incompatible with a training contract. Positive allowlist so that
// unknown/unresolved values never wrongly block. Per the legal/practical nature
// of a training contract, ANY prior-experience requirement is incoherent,
// including 'less_1y' ("Meno di 1 anno"). Only the zero-experience keys
// ('zero', 'experience_jp_zero') stay COMPATIBLE.
export const TRAINING_INCOMPATIBLE_EXPERIENCE_KEYS = [
  'less_1y',
  'experience_1',
  'experience_1_year',
  'between_1_3y',
  'between_3_5y',
  'experience_2_3_year',
  'experience_5_year',
  'between_5_10y',
  'more_10y',
  'experience_10_year',
];

export const isTrainingContractKey = (contractKey?: string): boolean =>
  !!contractKey && TRAINING_CONTRACT_KEYS.includes(contractKey);

export const experienceRequiresPriorExperience = (experienceKey?: string): boolean =>
  !!experienceKey && TRAINING_INCOMPATIBLE_EXPERIENCE_KEYS.includes(experienceKey);

/**
 * True when the contract type / experience combination is the contradiction we
 * want to block: a training contract that requires prior experience in the role.
 */
export const isTrainingContractIncompatibleWithExperience = (contractKey?: string, experienceKey?: string): boolean =>
  isTrainingContractKey(contractKey) && experienceRequiresPriorExperience(experienceKey);
